import { useEffect, useState } from "react"
import { useField, useFormikContext } from "formik"
import { DynamicConfigField, MeetingServiceConfigMode, MeetingServiceTabValues } from "../form/formTypes"
import { ConfigQuery, fetchTimelineResources, TimelineResourcesResponse } from "../../createOrderApi"
import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { getFormikFieldError } from "../form/helpers"

type Resource = Readonly<{
    id: string
    name: string
}>

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) return []

    const allResources: Resource[] = serviceCentersData.flatMap((service) =>
        service.resources.map((resource) => ({
            id: String(resource.id),
            name: resource.displayName || resource.name,
        })),
    )

    return allResources
}

type DeliveryLocationAutocompleteProps = {
    userId: number
    config: DynamicConfigField
}

export function DeliveryLocationAutocomplete({ userId, config }: DeliveryLocationAutocompleteProps) {
    const [search, setSearch] = useState("")
    const [open, setOpen] = useState(false)

    const autocompleteMode: MeetingServiceConfigMode = config.inputField?.mode || MeetingServiceConfigMode.LOAD_ON_OPEN

    const baseEnabled =
        (autocompleteMode === MeetingServiceConfigMode.SEARCH_AS_YOU_TYPE && search.length >= 2) ||
        (autocompleteMode === MeetingServiceConfigMode.LOAD_ON_OPEN && open)

    const [field, meta, helpers] = useField(config.key)
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)

    const [optionsSnapshot, setOptionsSnapshot] = useState(false)
    const shouldHydrateFromValue = Boolean(field.value) && !optionsSnapshot
    const enabled = baseEnabled || shouldHydrateFromValue

    const {
        data: options = [],
        isError,
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId,
            resourceTypes: "ROOM",
        },
        select: transformData,
        enabled,
    })

    useEffect(() => {
        if (options.length > 0 && !optionsSnapshot) {
            setOptionsSnapshot((prev) => !prev)
        }
    }, [options, optionsSnapshot])

    const handleInputChange = (_event: React.ChangeEvent<{}>, value: string) => {
        setSearch(value)
    }

    const handleChange = (_event: React.SyntheticEvent, value: any, reason?: string) => {
        if (value == null || reason === "clear") {
            helpers.setValue("", false)
            return
        }
        helpers.setValue(value.id, false)
    }

    return (
        <ApiQueryAutocomplete
            isLoading={enabled && isLoading}
            isError={isError}
            options={options}
            getOptionLabel={(option) => option.name || ""}
            value={options.find((opt) => opt.id === field.value) || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            size="small"
            slotProps={{
                textField: formikErrorProps,
            }}
            label={`${config?.label || config?.description}${config?.inputField?.isRequired ? " *" : ""}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
        />
    )
}
